/**
 * GitHub-style unified diff renderer for an action's before/after JSON.
 * Collapsed behind a toggle; renders added (green) / removed (red) / unchanged lines
 * with a +/- gutter and line numbers.
 */
import { useState } from 'react';

import { computeJsonDiff, IDiffLine } from '../utils/json-diff';

interface IJsonDiffViewProps {
    beforeJson: Record<string, any> | null;
    afterJson: Record<string, any> | null;
}

const GUTTER_SYMBOL: Record<IDiffLine['type'], string> = {
    added: '+',
    removed: '-',
    unchanged: ' ',
};

const JsonDiffView: React.FC<IJsonDiffViewProps> = ({ beforeJson, afterJson }) => {
    const [expanded, setExpanded] = useState(false);

    const hasBefore = beforeJson != null;
    const hasAfter = afterJson != null;
    if (!hasBefore && !hasAfter) {
        return null;
    }

    const lines = computeJsonDiff(beforeJson, afterJson);
    const addedCount = lines.filter((l) => l.type === 'added').length;
    const removedCount = lines.filter((l) => l.type === 'removed').length;

    return (
        <div className="json-diff">
            <button
                type="button"
                className="json-diff__toggle"
                onClick={() => setExpanded((prev) => !prev)}
            >
                {expanded ? '▾' : '▸'} {expanded ? 'Hide' : 'Show'} diff
                <span className="json-diff__stat json-diff__stat--added">+{addedCount}</span>
                <span className="json-diff__stat json-diff__stat--removed">−{removedCount}</span>
            </button>

            {expanded && (
                <div className="json-diff__body">
                    {lines.map((line, index) => (
                        <div key={index} className={`json-diff__line json-diff__line--${line.type}`}>
                            <span className="json-diff__lineno">{line.beforeLineNo ?? ''}</span>
                            <span className="json-diff__lineno">{line.afterLineNo ?? ''}</span>
                            <span className="json-diff__gutter">{GUTTER_SYMBOL[line.type]}</span>
                            <span className="json-diff__code">{line.text}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default JsonDiffView;
